import { ESdChangeReason, IAuthorizationDto, IProjectRole, IRoleDto, SpecialDIIds } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface ScreenAuthorizationProviderState {
  updateScreenAuthorization: (role: IRoleDto) => void;
  /** Saves a role (a new one gets its id here); resolves with that id once the project save is back. */
  updateRole: (role: IProjectRole) => Promise<string | undefined>;
  /** Resolves once the project write has landed, so a caller can serialise against it. */
  deleteRole: (roleId: string) => Promise<void>;
  isHiddenChanged: () => void;
  isDisabledChanged: () => void;
  allowAnonymousAccessChanged: () => void;
  allowAllRolesChanged: () => void;
  screenAuthorizationClipboard: string | null;
  setScreenAuthorizationClipboard: (value: string | null) => void;
}

export class ScreenAuthorizationProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ScreenAuthorizationProviderState
> {
  static defaultState: ScreenAuthorizationProviderState = {
    updateScreenAuthorization: () => {},
    updateRole: async () => undefined,
    deleteRole: () => Promise.resolve(),
    isHiddenChanged: () => {},
    isDisabledChanged: () => {},
    allowAnonymousAccessChanged: () => {},
    allowAllRolesChanged: () => {},
    screenAuthorizationClipboard: null,
    setScreenAuthorizationClipboard: () => {}
  };

  getProviderValues = (): ScreenAuthorizationProviderState => {
    return {
      updateScreenAuthorization: this.updateScreenAuthorization,
      updateRole: this.updateRole,
      deleteRole: this.deleteRole,
      isHiddenChanged: this.isHiddenChanged,
      isDisabledChanged: this.isDisabledChanged,
      allowAnonymousAccessChanged: this.allowAnonymousAccessChanged,
      allowAllRolesChanged: this.allowAllRolesChanged,
      screenAuthorizationClipboard: this.state.screenAuthorizationClipboard,
      setScreenAuthorizationClipboard: this.setScreenAuthorizationClipboard
    };
  };

  setScreenAuthorizationClipboard = (value: string | null) => {
    this.setState({ screenAuthorizationClipboard: value });
  };

  initSdAuthorization = () => {
    const sd = this.partial.screenProvider.getScreenDesign();

    if (this.isScreen() === true) {
      if (!sd.authorization) sd.authorization = {} as IAuthorizationDto;
      if (!sd.authorization.permittedRoles) sd.authorization.permittedRoles = [];
    }

    return sd;
  };

  // KOA-18448 — RETURNS the save so a caller can wait for it. The project write is a
  // read-modify-write against a diff baseline that only advances once its own response lands, so
  // a caller that fires and forgets reports success while its role is still in flight — and a
  // second write started in that window diffs against the same stale baseline. The role's id
  // comes back with it, which is how a dialog that just created one addresses it.
  updateRole = async (role: IProjectRole): Promise<string | undefined> => {
    if (!role) {
      return undefined;
    }
    const { project } = this.state;
    if (!project.roles) {
      project.roles = [];
    }
    if (role.id) {
      const editedRole = project.roles.find((p) => p.id == role.id);
      if (editedRole) {
        editedRole.name = role.name;
        editedRole.startingScreenId = role.startingScreenId;
      } else {
        // Extending a workspace role into this project (KOA-18634): it carries a canonical id this
        // project doesn't have yet, so add it with that id instead of minting a new one — that is
        // what keeps the same role from getting a second id across projects.
        project.roles.push(_.cloneDeep(role));
      }
    } else {
      role.id = Guid.create().toString();
      project.roles.push(_.cloneDeep(role));
    }

    await this.partial.projectProvider.updateProject(project, true, ESdChangeReason.OtherSetStates);
    return role.id;
  };

  deleteRole = (roleId: string): Promise<void> => {
    if (!roleId || roleId == "") {
      return Promise.resolve();
    }
    const { project } = this.state;
    if (!project.roles) {
      project.roles = [];
    }
    project.roles = project.roles.filter((p) => p.id != roleId);

    return this.partial.projectProvider.updateProject(project, true, ESdChangeReason.OtherSetStates);
  };

  defaultAuthorizationModel = () => {
    return {
      permittedRoles: [],
      hide: false,
      disable: false,
      allowAnonymousAccess: false,
      allowAllRoles: true
    } as IAuthorizationDto;
  };

  updateScreenAuthorization = (role: IRoleDto) => {
    const sd = this.initSdAuthorization();
    const dict = this.partial.screenProvider.getScreenDesignDictionary();
    if (this.isScreen() === true) {
      this.setUpdateScreenAuthorization(role, sd.authorization);
      this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
    } else {
      const di = dict[parseInt(this.getKuikaId(), 10)];

      if (di) {
        // Second condition is for satisfying ts.
        if (_.isEmpty(di.authorization) || !di.authorization) {
          di.authorization = this.defaultAuthorizationModel();
        }

        this.setUpdateScreenAuthorization(role, di.authorization);
        this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
      }
    }
  };

  setUpdateScreenAuthorization = (role: IRoleDto, authorization: IAuthorizationDto) => {
    if (authorization?.permittedRoles?.includes(role.id)) {
      authorization.permittedRoles.splice(authorization.permittedRoles.indexOf(role.id), 1);
    } else {
      if (!authorization.permittedRoles) authorization.permittedRoles = [];
      authorization.permittedRoles.push(role.id);
    }
  };

  isHiddenChanged = () => {
    const sd = this.initSdAuthorization();
    const dict = this.partial.screenProvider.getScreenDesignDictionary();
    if (this.isScreen() === true) {
      this.setIsHiddenChanged(sd.authorization);
      this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
    } else {
      const di = dict[parseInt(this.getKuikaId(), 10)];
      if (di) {
        if (!di.authorization) {
          di.authorization = this.defaultAuthorizationModel();
        }
        this.setIsHiddenChanged(di.authorization);
        this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
      }
    }
  };

  setIsHiddenChanged = (authorization: IAuthorizationDto) => {
    authorization.disable = authorization.hide;
    authorization.hide = !authorization.hide;
  };

  isDisabledChanged = () => {
    const sd = this.initSdAuthorization();
    const dict = this.partial.screenProvider.getScreenDesignDictionary();

    if (this.isScreen() === true) {
      this.setIsDisabledChanged(sd.authorization);
      this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
    } else {
      const di = dict[parseInt(this.getKuikaId(), 10)];

      if (di) {
        if (!di.authorization) {
          di.authorization = this.defaultAuthorizationModel();
        }
        this.setIsDisabledChanged(di.authorization);
        this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
      }
    }
  };

  setIsDisabledChanged = (authorization: IAuthorizationDto) => {
    authorization.hide = authorization.disable;
    authorization.disable = !authorization.disable;
  };

  allowAnonymousAccessChanged = () => {
    const sd = this.initSdAuthorization();
    const dict = this.partial.screenProvider.getScreenDesignDictionary();

    if (this.isScreen() === true) {
      this.setAllowAnonymousAccessChanged(sd.authorization);
      this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
    } else {
      const di = dict[parseInt(this.getKuikaId(), 10)];

      if (di) {
        if (!di.authorization) {
          di.authorization = this.defaultAuthorizationModel();
        }
        this.setAllowAnonymousAccessChanged(di.authorization);
        this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
      }
    }
  };

  setAllowAnonymousAccessChanged = (authorization: IAuthorizationDto) => {
    authorization.allowAnonymousAccess = !authorization.allowAnonymousAccess;
    authorization.allowAllRoles = true;
    authorization.hide = false;
    authorization.disable = false;
    authorization.permittedRoles = [];
  };

  allowAllRolesChanged = () => {
    const sd = this.initSdAuthorization();
    const dict = this.partial.screenProvider.getScreenDesignDictionary();

    if (this.isScreen() === true) {
      this.setAllowAllRolesChanged(sd.authorization);
      this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
    } else {
      // const di = ScreenDesignHelper.findDisplayItemBykuikaid(this.getKuikaId(), sd.displayItems);
      const di = dict[parseInt(this.getKuikaId(), 10)];
      if (di) {
        if (!di.authorization) {
          di.authorization = this.defaultAuthorizationModel();
        }
        this.setAllowAllRolesChanged(di.authorization);
        this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.UpdateAuth);
      }
    }
  };

  setAllowAllRolesChanged = (authorization: IAuthorizationDto) => {
    authorization.allowAllRoles = !authorization.allowAllRoles;
    authorization.allowAnonymousAccess = false;
    authorization.hide = !authorization.allowAllRoles;
    authorization.disable = false;
    if (authorization.allowAllRoles) {
      authorization.permittedRoles = [];
    }
  };

  isScreen = (): boolean => {
    return this.getKuikaId() === SpecialDIIds.rootDI.toString();
  };

  getKuikaId = () => {
    return this.state.selectedKuikaIds[0];
  };
}
